import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ControlPanel = ({ 
  isLiveMode, 
  onToggleLiveMode, 
  autoRefresh, 
  onToggleAutoRefresh, 
  refreshInterval, 
  onRefreshIntervalChange,
  onManualRefresh,
  onExportData,
  connectionStatus,
  lastUpdate
}) => {
  const [showSettings, setShowSettings] = useState(false);

  const refreshIntervals = [
    { value: 10, label: '10 seconds' },
    { value: 30, label: '30 seconds' },
    { value: 60, label: '1 minute' },
    { value: 300, label: '5 minutes' }
  ];

  const getConnectionStatusColor = () => {
    switch (connectionStatus) {
      case 'connected': return 'status-online';
      case 'connecting': return 'status-warning';
      case 'disconnected': return 'status-offline';
      default: return 'status-online';
    }
  };

  const getConnectionStatusText = () => {
    switch (connectionStatus) {
      case 'connected': return 'Connected';
      case 'connecting': return 'Connecting...';
      case 'disconnected': return 'Disconnected';
      default: return 'Connected';
    }
  };

  const formatLastUpdate = () => {
    if (!lastUpdate) return 'Never';
    
    const now = new Date();
    const updateTime = new Date(lastUpdate);
    const diff = Math.floor((now - updateTime) / 1000);

    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return updateTime?.toLocaleTimeString();
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Control Panel</h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowSettings(!showSettings)}
          iconName="Settings"
        >
          Settings
        </Button>
      </div>
      <div className="space-y-4">
        {/* Connection Status */}
        <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className={`status-indicator ${getConnectionStatusColor()}`}></div>
            <div>
              <div className="text-sm font-medium text-foreground">
                {getConnectionStatusText()}
              </div>
              <div className="text-xs text-muted-foreground">
                Last update: {formatLastUpdate()}
              </div>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onManualRefresh}
            iconName="RefreshCw"
            disabled={connectionStatus === 'connecting'}
          >
            Refresh
          </Button>
        </div>

        {/* Live Mode Toggle */}
        <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
          <div className="flex items-center space-x-3">
            <Icon 
              name={isLiveMode ? "Radio" : "Pause"} 
              size={20} 
              className={isLiveMode ? "text-success" : "text-muted-foreground"} 
            />
            <div>
              <div className="text-sm font-medium text-foreground">
                {isLiveMode ? "Live Mode" : "Historical Mode"}
              </div>
              <div className="text-xs text-muted-foreground">
                {isLiveMode ? "Real-time data streaming" : "Static data view"}
              </div>
            </div>
          </div>
          <Button
            variant={isLiveMode ? "default" : "outline"}
            size="sm"
            onClick={onToggleLiveMode}
          >
            {isLiveMode ? "Live" : "Historical"}
          </Button>
        </div>

        {/* Auto Refresh Settings */}
        {isLiveMode && (
          <div className="p-3 bg-muted/30 rounded-lg space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Icon 
                  name="RotateCcw" 
                  size={20} 
                  className={autoRefresh ? "text-primary" : "text-muted-foreground"} 
                />
                <div>
                  <div className="text-sm font-medium text-foreground">Auto Refresh</div>
                  <div className="text-xs text-muted-foreground">
                    {autoRefresh ? `Every ${refreshInterval}s` : "Disabled"}
                  </div>
                </div>
              </div>
              <Button
                variant={autoRefresh ? "default" : "outline"}
                size="sm"
                onClick={onToggleAutoRefresh}
              >
                {autoRefresh ? "On" : "Off"}
              </Button>
            </div>

            {autoRefresh && showSettings && (
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground">
                  Refresh Interval
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {refreshIntervals?.map((interval) => (
                    <button
                      key={interval?.value}
                      onClick={() => onRefreshIntervalChange && onRefreshIntervalChange(interval?.value)}
                      className={`px-3 py-2 text-xs rounded-md nav-transition ${
                        refreshInterval === interval?.value
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-background border border-border text-foreground hover:bg-muted'
                      }`}
                    >
                      {interval?.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Quick Actions */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-foreground">Quick Actions</h4>
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onExportData}
              iconName="Download"
              fullWidth
            >
              Export Data
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.print()}
              iconName="Printer"
              fullWidth
            >
              Print View
            </Button>
          </div>
        </div>

        {/* System Information */}
        {showSettings && (
          <div className="p-3 bg-muted/30 rounded-lg space-y-2">
            <h4 className="text-sm font-medium text-foreground">System Information</h4>
            <div className="space-y-1 text-xs text-muted-foreground">
              <div className="flex justify-between">
                <span>Dashboard Version:</span>
                <span>v2.1.0</span>
              </div>
              <div className="flex justify-between">
                <span>API Version:</span>
                <span>v1.8.3</span>
              </div>
              <div className="flex justify-between">
                <span>Data Sources:</span>
                <span>12 Active</span>
              </div>
              <div className="flex justify-between">
                <span>Uptime:</span>
                <span>99.8%</span>
              </div>
            </div>
          </div>
        )}

        {/* Emergency Actions */}
        <div className="p-3 bg-error/10 border border-error/20 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="AlertTriangle" size={16} className="text-error" />
            <span className="text-sm font-medium text-error">Emergency Controls</span>
          </div>
          <div className="space-y-2">
            <Button
              variant="outline"
              size="sm"
              fullWidth
              className="text-error border-error hover:bg-error hover:text-white"
            >
              Emergency Stop All
            </Button>
            <Button
              variant="outline"
              size="sm"
              fullWidth
              className="text-warning border-warning hover:bg-warning hover:text-white"
            >
              Maintenance Mode
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ControlPanel;